import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Optional accessible label rendered above the field. */
  label?: string;
  /** Inline validation / helper message under the field. */
  error?: string;
}

/**
 * Text field matching Figma Input: 44px surface field, default border,
 * secondary placeholder colour.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, label, error, id, disabled, ...props },
  ref,
) {
  const fieldId = id ?? props.name;

  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      {label ? (
        <label htmlFor={fieldId} className="text-label-s text-text-secondary">
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={fieldId}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && fieldId ? `${fieldId}-error` : undefined}
        className={cn(
          'h-11 w-full rounded-md border border-border-default bg-bg-surface px-4 py-3',
          'text-body-l text-text-primary placeholder:text-text-secondary',
          'outline-none transition-colors focus-visible:border-border-focus',
          'disabled:cursor-not-allowed disabled:opacity-60',
          error && 'border-status-danger',
        )}
        {...props}
      />
      {error ? (
        <p id={fieldId ? `${fieldId}-error` : undefined} className="text-caption text-status-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
});
